import { useCallback, useEffect, useState } from 'react';
import Popup, { ToolbarItem } from 'devextreme-react/popup';
import { Button, NumberBox, SelectBox, TextArea, Tooltip } from 'devextreme-react';
import { formatNumber } from 'devextreme/localization';
import moment from 'moment';
import { useApiOrders } from '../../../api/api.orders';
import { useApiConfiguration } from '../../../api/api.configuration';
import { useAppSelector } from '../../../contexts/hooks';
import { useScreenSize } from '../../../utils/media-query';
import { LiquidatedIcon, NotLiquidatedIcon } from '../../../images/icons';
import newToast from '../new-toast/NewToast';
import ModalConfirmation from '../popup-confirmation/PopupConfirmation';
import './LiquidarOrden.scss';

type PaymentItem = {
  Rof_Id?: number | null;
  Rof_Fecha?: Date | null;
  Rof_Fp_Id: string | null;
  Rof_Valor: number | null;
  Rof_Nota?: string | null;
};

interface LiquidarOrdenProps {
  /** Id de la orden asociada (Ord_Id / POrd_Ord_Id). */
  idOrden: number;
  /** Valor total de la orden (se usa para el restante y el estado de liquidación). */
  valorTotal: number;
  /** Callback opcional luego de liquidar correctamente. */
  onLiquidated?: () => void;
}

/**
 * Ícono (verde = liquidado / gris = no liquidado) con su modal de liquidación.
 * Los abonos se consultan de forma asíncrona para no bloquear la interfaz.
 * Solo se muestra si el usuario tiene el permiso de liquidar (Prm_FactCajaLiquidar).
 */
export const LiquidarOrden = ({ idOrden, valorTotal, onLiquidated }: LiquidarOrdenProps) => {
  const { isXSmall } = useScreenSize();
  const { GetCashierOrderPass, CreateCashierOrder } = useApiOrders();
  const { getPaymentMethods } = useApiConfiguration();
  const Prm_FactCajaLiquidar = useAppSelector((state) => state.userInfo.Permissions?.Prm_FactCajaLiquidar);
  const Prm_FactCaja_U = useAppSelector((state) => state.userInfo.Permissions?.Prm_FactCaja_U);

  // Abonos previos de la orden (lo que devuelve GetCashierOrderPass). null = aún sin cargar.
  const [abonos, setAbonos] = useState<any[] | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [paymentItems, setPaymentItems] = useState<PaymentItem[]>([]);
  const [modalConfirmZeroValues, setModalConfirmZeroValues] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!idOrden) return;
    setLoadingStatus(true);
    try {
      const result = await GetCashierOrderPass(idOrden);
      setAbonos(Array.isArray(result) ? result : []);
    } finally {
      setLoadingStatus(false);
    }
  }, [idOrden]);

  // Consultar los abonos de forma asíncrona (no bloquea la UI).
  useEffect(() => {
    if (Prm_FactCajaLiquidar) {
      fetchStatus();
    }
  }, [fetchStatus, Prm_FactCajaLiquidar]);

  const sumaAbonosGuardados = (abonos || []).reduce((acc, cur) => acc + (cur.Rof_Valor || 0), 0);
  const liquidado = valorTotal > 0 && sumaAbonosGuardados >= valorTotal;

  const handleOpenModal = useCallback(() => {
    if (abonos === null) return;
    if (paymentMethods.length === 0) {
      getPaymentMethods().then(setPaymentMethods);
    }
    const nuevos = !liquidado ? [{ Rof_Fp_Id: null, Rof_Valor: null }] : [];
    setPaymentItems([...abonos, ...nuevos]);
    setModalVisible(true);
  }, [abonos, liquidado, paymentMethods, getPaymentMethods]);

  const onCancelClick = useCallback(() => {
    setModalVisible(false);
  }, []);

  const handleAddPaymentItem = () => {
    setPaymentItems([...paymentItems, { Rof_Fp_Id: null, Rof_Valor: null, Rof_Nota: null }]);
  };

  const handlePaymentItemChange = (index: number, field: keyof PaymentItem, value: any) => {
    const updatedItems = [...paymentItems];
    updatedItems[index][field] = value;
    setPaymentItems(updatedItems);
  };

  const proceedWithLiquidation = useCallback(() => {
    const abonosAGuardar = paymentItems.map((item) => ({
      ...item,
      Rof_Ord_Id: idOrden,
      Rof_Valor: item.Rof_Valor ?? 0,
    }));

    setLoading(true);
    CreateCashierOrder(abonosAGuardar)
      .then((res) => {
        if (res !== null) {
          setModalVisible(false);
          setPaymentItems([]);
          fetchStatus();
          onLiquidated?.();
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [paymentItems, idOrden, CreateCashierOrder, fetchStatus, onLiquidated]);

  const handleConfirmLiquidar = useCallback(() => {
    if (!Prm_FactCajaLiquidar && !Prm_FactCaja_U) {
      newToast('WARNING', 'No tiene permisos para liquidar o editar la caja.', 5);
      return;
    }

    const isValid = paymentItems.every((item) => item.Rof_Fp_Id);
    if (!isValid) {
      newToast('WARNING', 'Todos los abonos deben tener forma de pago.', 5);
      return;
    }

    const hasZeroValues = paymentItems.some((item) => (item.Rof_Valor ?? 0) === 0);
    if (hasZeroValues) {
      setModalConfirmZeroValues(true);
      return;
    }

    proceedWithLiquidation();
  }, [paymentItems, Prm_FactCaja_U, Prm_FactCajaLiquidar, proceedWithLiquidation]);

  const handleConfirmZeroValues = useCallback(() => {
    setModalConfirmZeroValues(false);
    proceedWithLiquidation();
  }, [proceedWithLiquidation]);

  const handleCancelZeroValues = useCallback(() => {
    setModalConfirmZeroValues(false);
  }, []);

  // Sin permiso de liquidar no se muestra nada.
  if (!Prm_FactCajaLiquidar) return null;

  const sumaAbonos = paymentItems.reduce((acc, item) => acc + (item.Rof_Valor || 0), 0);
  const iconId = `liquidar-orden-${idOrden}`;

  const cargando = loadingStatus || abonos === null;

  return (
    <>
      {/* El botón se renderiza siempre (deshabilitado mientras carga) para que el toolbar no se reacomode */}
      <Button id={iconId} icon={liquidado ? LiquidatedIcon : NotLiquidatedIcon} text='Liquidar' stylingMode='text' disabled={cargando} onClick={handleOpenModal} />
      {!cargando && (
        <Tooltip target={`#${iconId}`} showEvent='mouseenter' hideEvent='mouseleave' position='top'>
          {liquidado ? 'Orden liquidada' : 'Liquidar orden'}
        </Tooltip>
      )}

      {modalVisible && (
        <Popup title='Liquidar orden' visible={modalVisible} showCloseButton={true} onHiding={onCancelClick} fullScreen={isXSmall} width={500} height='auto'>
          <div className='plain-styled-form'>
            <div className='amount-to-liquidate'>
              <div className={`amount-label ${valorTotal !== sumaAbonos ? 'error' : 'succcess'}`}>
                Valor a liquidar: &nbsp; <strong>{formatNumber(valorTotal, { type: 'currency' })}</strong>
              </div>
              <div className={`amount-label ${valorTotal !== sumaAbonos ? 'error' : 'succcess'}`}>
                Valor restante: &nbsp;
                <strong>{formatNumber(valorTotal - sumaAbonos, { type: 'currency' })}</strong>
              </div>
            </div>
            <div className='payment-items-list'>
              {paymentItems.map((item, index) => {
                const isNuevo = !item.Rof_Id;
                const canEditMedioPago = isNuevo || Prm_FactCaja_U;
                const canEditValor = isNuevo || Prm_FactCaja_U;
                return (
                  <div key={index} className={`payment-item-card ${canEditMedioPago ? '' : 'locked'}`}>
                    <div className='payment-item-header'>{item.Rof_Id ? `Abono #${index + 1}: ${moment(item.Rof_Fecha).format('DD/MM/YYYY hh:mm a')}` : `Nuevo abono #${index + 1}`}</div>
                    <div className='payment-item-body'>
                      <div className='payment-item-field'>
                        <SelectBox dataSource={paymentMethods} displayExpr='Fp_Nombre' valueExpr='Fp_Id' value={item.Rof_Fp_Id} disabled={!canEditMedioPago} placeholder='Forma de pago' onValueChanged={(e) => canEditMedioPago && handlePaymentItemChange(index, 'Rof_Fp_Id', e.value)} />
                      </div>
                      <div className='payment-item-field'>
                        <NumberBox
                          value={item.Rof_Valor || 0}
                          format='currency'
                          placeholder='Valor'
                          disabled={!canEditValor}
                          onValueChanged={(e) => canEditValor && handlePaymentItemChange(index, 'Rof_Valor', e.value)}
                          buttons={
                            isNuevo && canEditValor
                              ? [
                                  {
                                    name: 'fillRemaining',
                                    location: 'after',
                                    options: {
                                      icon: 'money',
                                      type: 'default',
                                      stylingMode: 'text',
                                      hint: 'Llenar con valor restante',
                                      onClick: () => {
                                        const valorRestante = valorTotal - sumaAbonos;
                                        if (valorRestante > 0) {
                                          handlePaymentItemChange(index, 'Rof_Valor', valorRestante);
                                        }
                                      },
                                    },
                                  },
                                ]
                              : undefined
                          }
                        />
                      </div>
                      <div className='payment-item-field payment-item-notes'>
                        <TextArea value={item.Rof_Nota || ''} placeholder='Notas (opcional)' disabled={!canEditMedioPago} height={60} onValueChanged={(e) => canEditMedioPago && handlePaymentItemChange(index, 'Rof_Nota', e.value)} />
                      </div>
                      {isNuevo && (
                        <div className='payment-item-delete'>
                          <Button
                            icon='trash'
                            type='danger'
                            stylingMode='text'
                            onClick={() => {
                              const updated = [...paymentItems];
                              updated.splice(index, 1);
                              setPaymentItems(updated);
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <ToolbarItem toolbar='bottom' location='after' disabled={loading} visible={true}>
            <div className='form-popup-buttons-container'>
              {valorTotal !== sumaAbonos && <Button text='Nueva forma de pago' icon='plus' type='normal' onClick={handleAddPaymentItem} />}
              <Button text={loading ? 'Guardando...' : 'Guardar cambios'} icon='check' type='default' onClick={handleConfirmLiquidar} />
            </div>
          </ToolbarItem>
        </Popup>
      )}

      <ModalConfirmation visible={modalConfirmZeroValues} message='Seguro que desea liquidar valores en 0' onConfirm={handleConfirmZeroValues} onCancel={handleCancelZeroValues} buttons='No/Yes' />
    </>
  );
};

export default LiquidarOrden;
